import { ConvertHelper, DataProvider } from "../helpers"

import { Enums } from "../models/common"
import { ColumnInfo, MeasureCollection } from "../models/custom"

import { OpActor, OpConsumer, OpConsumerTransaction, OpLocation } from "../models/core"
import { OpConsumerNotificationCollection } from "../models/custom/op-consumer-notification-collection"
import { OpConsumerNotificationToSend } from "../models/custom/op-consumer-notification-to-send"

export class DataProviderConsumer {

    // #region Ctor

    constructor(private dataProvider: DataProvider) { }

    // #endregion


    // #region Public Methods

    public commission = (idConsumer: number): JQueryPromise<void> => {
        return this.dataProvider
            .sendRequestWithSession('OpxConsumerCommission', { idConsumer: idConsumer, idPaymentModule: Enums.PaymentModule.WebSIMAX }, null)
    }

    public consumerNewPayment = (idLocation: number, paymentAmount: number): JQueryPromise<void> => {
        return this.dataProvider.sendRequestWithSession('OpxConsumerNewPayment', { IdLocation: idLocation, PaymentAmount: paymentAmount }, null)
    }

    public consumerUpdateOwed = (idConsumer: number, owedValue: number): JQueryPromise<void> => {
        return this.dataProvider.sendRequestWithSession('OpxConsumerUpdateOwed', { idConsumer: idConsumer, owedValue: owedValue }, null)
    }

    public consumerRollbackTopUpTransaction = (idConsumer: number, idLocation: number, idConsumerTransaction: number): JQueryPromise<void> => {
        return this.dataProvider.sendRequestWithSession('OpxConsumerRollbackTopUpTransaction', { idConsumer: idConsumer, idLocation: idLocation, idConsumerTransaction: idConsumerTransaction }, null)
    }

    public decommission = (idConsumer: number): JQueryPromise<void> => {
        return this.dataProvider
            .sendRequestWithSession('OpxConsumerDecommission', { idConsumer: idConsumer, idPaymentModule: Enums.PaymentModule.WebSIMAX }, null)
    }

    public getCashierPanelLocationFilter = (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => {
        return this.dataProvider.sendFilterRequestWithSession<OpConsumer>(columns, pageNbr, pageSize, customWhereClause, Enums.ReferenceType.IdLocation, 'OpxCashierPanelLocationFilter', 'OpxCashierPanelLocationFilterResult')
    }

    public deleteConsumer = (opObj: OpConsumer): JQueryPromise<OpConsumer> => {
        opObj.opState = Enums.OpChangeState.Modified
        opObj.dataList.setValue(true, Enums.DataType.HELPER_IS_BLOCKED)

        //Aktor musi być utworzony, inaczej WS zwraca błąd
        if (opObj.actor == null) {
            opObj.actor = new OpActor(null)
        }

        return this.saveConsumer(opObj)
    }

    public getConsumer = (id: number): JQueryPromise<OpConsumer> => {
        return this.dataProvider.sendRequestWithSession('OpxGetConsumer', { IdConsumer: id }, 'OpxGetConsumerResult').then((data: any) => {
            const result: OpConsumer = new OpConsumer(data)

            OpConsumer.loadNavigationProperties(this.dataProvider, [result])

            return result
        })
    }

    public getConsumerFilter = (columns: ColumnInfo[], pageNbr: number, pageSize: number, customWhereClause: string) => {
        return this.dataProvider.sendFilterRequestWithSession<OpConsumer>(columns, pageNbr, pageSize, customWhereClause, Enums.ReferenceType.IdConsumer, 'OpxConsumerFilter', 'OpxConsumerFilterResult')
    }

    public getConsumerLocation = (idLocation: number): JQueryPromise<OpLocation> => {
        return this.dataProvider
            .sendRequestWithSession('OpxGetConsumerLocation', { IdLocation: idLocation }, 'OpxGetConsumerLocationResult')
            .then((response: any) => {
                if (response != null) {
                    const result: OpLocation = new OpLocation(response)

                    OpLocation.loadNavigationProperties(this.dataProvider, [result])

                    return result
                }

                return null
            });
    }

    public getConsumerMeasurementFilter = (idLocation: number, idDataType: number[], dateFrom: Date, dateTo: Date, topCount: number = null, customWhereClause: string = null) => {
        return this.dataProvider
            .sendRequestWithSession(
                'OpxConsumerMeasurementFilter',
                {
                    IdLocation: idLocation,
                    IdDataType: idDataType,
                    Time: ConvertHelper.dateRangeToTypeDateTimeCode(dateFrom, dateTo),
                    topCount: topCount,
                    customWhereClause: customWhereClause,
                    commandTimeout: 0
                },
                'OpxConsumerMeasurementFilterResult')
            .then((result: any[]) => {
                return result.map((obj: any) => new MeasureCollection(obj))
            })
    }

    public getConsumerTransactionFilter = (idLocation: number, dateFrom: Date, dateTo: Date, topCount: number = null, customWhereClause: string = null): JQueryPromise<OpConsumerTransaction[]> => {
        return this.dataProvider
            .sendRequestWithSession(
                'OpxConsumerTransactionFilter',
                {
                    IdLocation: idLocation,
                    Time: ConvertHelper.dateRangeToTypeDateTimeCode(dateFrom, dateTo),
                    topCount: topCount,
                    customWhereClause: customWhereClause,
                    commandTimeout: 0
                },
                'OpxConsumerTransactionFilterResult')
            .then((response: any[]) => {
                return response.map((obj: any) => new OpConsumerTransaction(obj))
            })
    }

    public saveConsumer = (opObj: OpConsumer): JQueryPromise<OpConsumer> => {
        return this.dataProvider
            .sendRequestWithSession('OpxSaveConsumer', { ToBeSaved: opObj.toJSON() }, 'OpxSaveConsumerResult')
            .then((data: any) => {
                const result: OpConsumer = new OpConsumer(data)

                OpConsumer.loadNavigationProperties(this.dataProvider, [result])

                return result
            })
    }

    public getConsumerNotifications = (idConsumer: number): JQueryPromise<OpConsumerNotificationCollection> => {
        return this.dataProvider
            .sendRequestWithSession('OpxGetConsumerNotifications', { idConsumer: idConsumer }, 'OpxGetConsumerNotificationsResult')
            .then((response: any) => {
                if (response != null) {
                    return new OpConsumerNotificationCollection(response)
                }
                return null
            })
    }

    public refreshCreditOnDevice = (idConsumer: number, idLocation: number): JQueryPromise<void> => {
        return this.dataProvider
            .sendRequestWithSession('OpxConsumerRefreshCreditOnDevice', { idConsumer: idConsumer, idLocation: idLocation, idPaymentModule: Enums.PaymentModule.WebSIMAX }, null)
    }

    public saveConsumerNotifications = (consumerNotificationCollection: OpConsumerNotificationCollection): JQueryPromise<OpConsumerNotificationCollection> => {
        return this.dataProvider
            .sendRequestWithSession('OpxSaveConsumerNotifications', { toBeSaved: consumerNotificationCollection.toJSON() }, 'OpxSaveConsumerNotificationsResult')
            .then((response: any) => {
                if (response != null) {
                    return new OpConsumerNotificationCollection(response)
                }
                return null
            })
    }

    public sendConsumerNotifications = (notificationToSend: OpConsumerNotificationToSend): JQueryPromise<boolean> => {
        return this.dataProvider
            .sendRequestWithSession('OpxSendConsumerNotification', { notificationToSend: notificationToSend.toJSON() }, 'OpxSendConsumerNotificationResult')
    }

    // #endregion
}

